import { DropdownChevron } from '@components'
import cn from '@integrations/classnames'
import { uiSize } from '@utils'
import { useGetProfileQuery, useLogoutQry } from 'mmfintech-backend-api'
import { tr, useDropDownMenu } from 'mmfintech-commons'
import { useMerchantSwitch } from '../../../MerchantSwitchMenu/useMerchantSwitch'
import surfaceStyles from '../../HeaderMenuSurface/HeaderMenuSurface.module.scss'
import UserMenuItems from '../UserMenuItems'

function initialOf(name?: string) {
  return name ? name.substring(0, 1).toUpperCase() : '?'
}

function AccountMenu() {
  const { logout } = useLogoutQry()
  const { data: profile } = useGetProfileQuery(null)
  const { merchantList, canSwitch, currentMerchantId, currentMerchantLabel, selectMerchantById } = useMerchantSwitch()
  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()

  const merchantName = currentMerchantLabel || profile?.email || ''

  function handleLogoutClick() {
    hideDropDown()
    void logout()
  }

  function handleMerchantClick(merchantId: number) {
    hideDropDown()
    void selectMerchantById(merchantId)
  }

  return (
    <div
      className={cn(surfaceStyles['header-menu-wrapper'], 'account-menu-wrapper', 'header-icons-wrapper')}
      ref={setRef}
    >
      <button
        type='button'
        className={cn(
          surfaceStyles['header-menu-trigger'],
          surfaceStyles['header-menu-pill'],
          'header-menu-pill',
          'account-menu-pill',
          {
            [surfaceStyles['opened']]: visible,
            opened: visible
          }
        )}
        aria-haspopup='menu'
        aria-expanded={visible}
        aria-label={merchantName}
        onClick={toggleDropDown}
        data-test='settings-icon'
      >
        <span className={cn(surfaceStyles['header-menu-avatar'], 'icon-letter')}>{initialOf(merchantName)}</span>
        <span className={cn(surfaceStyles['header-menu-label'], 'name')}>{merchantName}</span>
        <DropdownChevron className={cn('arrow')} expanded={visible} />
      </button>

      {visible && (
        <nav
          role='menu'
          className={cn(surfaceStyles['header-menu-dropdown'], surfaceStyles['opened'], 'header-icons-dropdown')}
        >
          <div className={cn(surfaceStyles['header-menu-identity'], 'account-menu-identity')}>
            <span className={cn(surfaceStyles['header-menu-avatar'], 'header-menu-avatar')}>
              {initialOf(merchantName)}
            </span>
            <div>
              <div className={cn(surfaceStyles['header-menu-identity-name'], 'header-menu-identity-name')}>
                {merchantName}
              </div>
              {profile?.email && profile.email !== merchantName && (
                <div className={cn(surfaceStyles['header-menu-identity-email'], 'header-menu-identity-email')}>
                  {profile.email}
                </div>
              )}
            </div>
          </div>

          <UserMenuItems
            showIcons
            onClick={hideDropDown}
            className={cn(surfaceStyles['header-menu-option'], 'menu-item', 'dropdown-option')}
          />

          {canSwitch && (
            <>
              <div className={cn(surfaceStyles['header-menu-divider'], 'header-menu-divider')} />
              <div className={cn(surfaceStyles['header-menu-caption'], 'header-menu-caption')}>
                {tr('FRONTEND.HEADER.SWITCH_MERCHANT', 'Switch to')}
              </div>
              <ul className={cn(surfaceStyles['header-menu-list'], 'header-menu-list')} data-test='merchant-menu-list'>
                {merchantList.map((v) => (
                  <li key={v.merchantId}>
                    <button
                      type='button'
                      className={cn(surfaceStyles['header-menu-option'], 'dropdown-option', {
                        [surfaceStyles['selected']]: v.merchantId === currentMerchantId
                      })}
                      onClick={() => handleMerchantClick(v.merchantId)}
                      data-test='merchant-menu-item'
                    >
                      <span className={cn(surfaceStyles['header-menu-avatar'], 'header-menu-avatar')}>
                        {initialOf(v.displayName)}
                      </span>
                      {v.displayName}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className={cn(surfaceStyles['header-menu-divider'], 'header-menu-divider')} />
          <button
            type='button'
            role='menuitem'
            className={cn(surfaceStyles['header-menu-option'], 'menu-item', 'logout')}
            onClick={handleLogoutClick}
          >
            <svg
              width={uiSize(15)}
              height={uiSize(15)}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9' />
            </svg>
            {tr('FRONTEND.HEADER.LOGOUT', 'Log out')}
          </button>
        </nav>
      )}
    </div>
  )
}

export default AccountMenu
